import axios from 'axios';
import { toast } from '@/components/ui/use-toast';

const API_ORIGIN: string = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

const api = axios.create({
    baseURL: `${API_ORIGIN}/api/v1`,
    withCredentials: true,
    withXSRFToken: true,
    headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
});

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/** Auth flows (login/logout) render their own inline errors — a duplicate global toast would be noise. */
function isAuthRequest(url?: string): boolean {
    return !!url && url.includes('/auth/');
}

function firstValidationError(errors?: Record<string, string[]>): string | null {
    if (!errors) return null;
    const first = Object.values(errors)[0];
    return first?.[0] ?? null;
}

api.interceptors.response.use(
    (response) => {
        const method = response.config.method?.toLowerCase();

        if (method && MUTATING_METHODS.has(method) && !isAuthRequest(response.config.url)) {
            const message = response.data?.message;
            if (message && typeof message === 'string') {
                toast({ variant: 'success', description: message });
            }
        }

        return response;
    },
    (error) => {
        if (error.response?.status === 401) {
            window.dispatchEvent(new CustomEvent('aipa:unauthenticated'));
            return Promise.reject(error);
        }

        const method = error.config?.method?.toLowerCase();

        if (method && MUTATING_METHODS.has(method) && !isAuthRequest(error.config?.url)) {
            const data = error.response?.data;
            const message = firstValidationError(data?.errors) ?? data?.message ?? 'Something went wrong. Please try again.';
            toast({ variant: 'destructive', title: 'Error', description: message });
        }

        return Promise.reject(error);
    },
);

/**
 * Lets a platform admin act inside a chosen tenant's context on an otherwise
 * tenant-scoped endpoint — ResolveTenant (backend) already honors this header
 * for platform admins (see frontend/src/modules/super-admin/hooks/use-tenants.ts's
 * useAdminApplications/useAssignAdminApplications for the established
 * precedent). Reused by the Assistant Builder so the exact same tenant-scoped
 * AI Assistant hooks/components work for both a tenant user and a super admin
 * managing that tenant's assistant.
 */
export function tenantHeaderConfig(adminTenantId?: number): { headers: { 'X-Tenant-ID': string } } | Record<string, never> {
    return adminTenantId ? { headers: { 'X-Tenant-ID': String(adminTenantId) } } : {};
}

export async function ensureCsrfCookie(): Promise<void> {
    await axios.get('/sanctum/csrf-cookie', { baseURL: API_ORIGIN, withCredentials: true });
}

/**
 * For the public, unauthenticated `widget/*` endpoints only (Test Chat here
 * in the SPA, and the home page's own live demo widget) — deliberately NOT
 * `withCredentials`. Those endpoints answer with a literal
 * `Access-Control-Allow-Origin: *` (see backend AllowWidgetCors — a real
 * third-party embed can come from any origin, so no fixed allowlist is
 * possible), and per the CORS spec a browser refuses to expose a wildcarded
 * response to a *credentialed* request — cookies would be silently blocked
 * client-side with no server-visible error. The widget's own auth is a
 * bearer token returned in the response body, never a cookie, so there's
 * nothing to lose by leaving credentials off here.
 */
const publicApi = axios.create({
    baseURL: `${API_ORIGIN}/api/v1`,
    headers: { Accept: 'application/json' },
});

export { api, API_ORIGIN, publicApi };
